<template>
  <div class="main survey-form">
    <FormWrapper method="POST" :action="formAction">
      <div class="title-bar pb-2">
        <h1 class="mb-1">{{ surveyable.name }}</h1>
        <h4 class="text-primary">{{ survey.name }}</h4>
      </div>
      <div class="content p-3">
        <div v-if="isClosed" class="alert alert-danger">
          This survey is now closed.
        </div>
        <div v-else-if="!isOpen" class="alert alert-warning">
          This survey is not yet available. Please check back later.
        </div>
        <div v-else class="container">
          <div class="row panel py-3 py-lg-5">
            <div class="col-12 px-3">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
            </div>
            <div class="col-12 px-3 px-lg-5">
              <template v-for="field in survey.fields">
                <template v-if="fieldType(field) == 'text'">
                  <Field
                    type="text"
                    :key="field.id"
                    :name="`submission[${field.slug}]`"
                    :label="field.label"
                    v-model="responses[field.slug]"
                    v-show="isVisible(field)"
                    class="mb-4"
                  />
                </template>
                <template v-else-if="fieldType(field) == 'textarea'">
                  <Field
                    type="textarea"
                    :key="field.id"
                    :name="`submission[${field.slug}]`"
                    :label="field.label"
                    v-model="responses[field.slug]"
                    v-show="isVisible(field)"
                    class="mb-4"
                  />
                </template>
                <template v-else-if="fieldType(field) == 'select'">
                  <Field
                    type="select"
                    :key="field.id"
                    :name="`submission[${field.slug}]`"
                    :label="field.label"
                    v-model="responses[field.slug]"
                    :options="fieldOptions(field)"
                    v-show="isVisible(field)"
                    class="mb-4"
                    :hide-select-empty-option="true"
                  />
                </template>
                <template v-else-if="fieldType(field) == 'checkboxgroup'">
                  <div
                    v-show="isVisible(field)"
                    class="checkbox-group mb-4"
                    :key="field.id"
                  >
                    <div class="label">{{ field.label }}</div>
                    <div class="group">
                      <Field
                        v-for="(opt, i) in fieldOptions(field)"
                        :key="i"
                        type="checkbox"
                        :name="`submission[${field.slug}][]`"
                        :id="`${field.slug}_${i}`"
                        :label="opt.label"
                        :checkbox-value="opt.value"
                        v-model="responses[`${field.slug}-${i}`]"
                      />
                    </div>
                  </div>
                </template>
                <template v-else-if="fieldType(field) == 'radiogroup'">
                  <div
                    v-show="isVisible(field)"
                    class="radio-group mb-4"
                    :key="field.id"
                  >
                    <div class="label">{{ field.label }}</div>
                    <div class="group">
                      <Field
                        v-for="(opt, i) in fieldOptions(field)"
                        :key="i"
                        type="radio"
                        :name="`submission[${field.slug}]`"
                        :id="`${field.slug}_${i}`"
                        :label="opt.label"
                        :radio-value="opt.value"
                        v-model="responses[field.slug]"
                      />
                    </div>
                  </div>
                </template>
                <template v-else-if="fieldType(field) == 'rating'">
                  <RatingField
                    :key="field.id"
                    :name="`submission[${field.slug}]`"
                    :label="field.label"
                    v-model="responses[field.slug]"
                    v-show="isVisible(field)"
                    class="mb-4"
                  />
                </template>
                <template v-else-if="fieldType(field) == 'netpromoter'">
                  <NetPromoterField
                    :key="field.id"
                    :name="`submission[${field.slug}]`"
                    :label="field.label"
                    v-model="responses[field.slug]"
                    v-show="isVisible(field)"
                    class="mb-4"
                  />
                </template>
                <template v-else-if="fieldType(field) == 'html'">
                    <div v-html="field.value"></div>
                </template>
              </template>
            </div>
            <div class="col-12 py-3 text-center">
              <button
                type="submit"
                class="btn btn-success w-75 mb-3 w-sm-auto mb-sm-0 mr-sm-2"
              >
                Submit
              </button>
              <a class="btn btn-info w-75 w-sm-auto" href="/">Cancel & Exit</a>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import HtmlField from 'components/forms/HtmlField'

export default {
    components: {HtmlField},
    mixins: [DisplaysAlerts],
  props: {
    survey: { type: Object, required: true },
    surveyable: { type: Object, required: true },
    type: { type: String, required: true },
    isOpen: { type: Boolean, default: true },
    isClosed: { type: Boolean, default: false },
  },
  data() {
    return {
      responses: {},
    };
  },
  computed: {
    formAction() {
      var routeName = `surveys.${this.type}.store`;
      var routeVar = this.type.replace("-", "_");
      var routeParam = {};
      routeParam[routeVar] = this.surveyable.id;
      return this.route(routeName, routeParam);
    },
  },
  methods: {
    conditionMet(condition) {
      var targetField = this.getFieldById(condition.target_id);
      let targetValue = null

      if (this.fieldType(targetField) === 'checkboxgroup') {
          const valueIndex = targetField.value.indexOf(condition.value);
          const responseKey = `${targetField.slug}-${valueIndex}`;
          if (responseKey in this.responses && this.responses[responseKey]) {
              targetValue = condition.value;
          }
      } else {
          targetValue = targetField.slug in this.responses
              ? this.responses[targetField.slug]
              : null;
      }

      return condition.operand == this.$root.constants.CONDITIONAL_OPERAND_IS
        ? condition.value == targetValue
        : condition.value != targetValue;
    },
    fieldType(field) {
      var type = field.type.replace("App\\Models\\Forms\\FieldTypes\\", "");
      type = type.replace("Field", "");
      return type.toLowerCase();
    },
    fieldOptions(field) {
      var options = [];
      if (_.isArray(field.value) && field.value.length > 0) {
        field.value.forEach((val) => {
          options.push({ label: val, value: val });
        });
      }
      return options;
    },
    getFieldById(id) {
      return this.survey.fields.find((field) => {
        return field.id == id;
      });
    },
    isVisible(field) {
      if (!field.is_conditional || field.conditions.length == 0) {
        return true;
      }
      var minConditions =
        field.conditional_scope == this.$root.constants.CONDITIONAL_SCOPE_ALL
          ? field.conditions.length
          : 1;
      var conditionsMet = 0;
      field.conditions.forEach((condition) => {
        if (this.conditionMet(condition)) {
          conditionsMet++;
        }
      });
      if (conditionsMet >= minConditions) {
        return (
          field.conditional_visibility ==
          this.$root.constants.CONDITIONAL_VISIBILITY_SHOW
        );
      } else {
        return (
          field.conditional_visibility ==
          this.$root.constants.CONDITIONAL_VISIBILITY_HIDE
        );
      }
    },
    preloadResponses() {
      var responses = {};
      this.survey.fields.forEach((field) => {
        responses[field.slug] = field.response;
      });
      this.responses = responses;
    },
  },
  mounted() {
    // console.log(this.survey.fields);
    this.preloadResponses();
  },
};
</script>